import { queryOptions } from '@tanstack/react-query'
import { apiClient } from '~/lib/client'

import { handleApiError, handleResponse } from '~/utils/response'

import { ReminderKeys } from '~/features/auth/api/keys'
import { GetActiveMedicationsResponse, GetCurrentProcessingResponse, GetMedicalHistoryResponse, GetNotificationReminderValueResponse, GetPersonalMedicalResponse, GetPrescriptionMedicationResponse, GetRefillResponse } from '../types'
import { medicationsKeys } from './keys'
import { formatRefillDate, formatRefillDateAbbreviated } from '@workspace/lib/date'


export const medicalsMedicationQueryOptions = (id1: string, id2: string) => {
    return queryOptions({
        queryKey: medicationsKeys.medication(id1, id2),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/medicals/medications/${id1}/${id2}`);

                const result = await handleResponse<GetPersonalMedicalResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },

    });
};

export const activeMedicationsQueryOptions = () => {
    return queryOptions({
        queryKey: medicationsKeys.activeMedications(),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/medicals/medications/active`);

                const result = await handleResponse<GetActiveMedicationsResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },
    });
};

export const medicalsMedicationsRefillHistoryQueryOptions = () => {
    return queryOptions({
        queryKey: medicationsKeys.refillHistory(),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/medicals/medications/refill/history`);

                const result = await handleResponse<GetMedicalHistoryResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },

    });
};

export const medicalsMedicationsRefillProcessingQueryOptions = () => {
    return queryOptions({
        queryKey: medicationsKeys.refillProcessing(),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/medicals/medications/refill/processing`);

                const result = await handleResponse<GetCurrentProcessingResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },
        select(data) {
            const processingRefills = data?.processing_medications?.processing_refills
            const processingMedications = data?.processing_medications?.processing_medications

            console.log("processingRefills", processingRefills)
            console.log("processingMedications", processingMedications)
            
            return processingRefills?.map((refill) => ({
                ...refill,
                name: refill.medication.name,
                delivery_date: formatRefillDate(refill.delivery_date),
                request_date: formatRefillDate(refill.request_date),
                progress: [
                    {
                      status: 'completed',
                      label: 'Requested',
                      date: formatRefillDateAbbreviated(refill.request_date)
                    },
                    {
                      status: 'completed',
                      label: 'Approved',
                      date: formatRefillDateAbbreviated(refill.request_date)
                    },
                    {
                      status: 'pending',
                      label: 'Order Placed',
                      date: formatRefillDateAbbreviated(refill.request_date)
                    },
                    {
                      status: 'pending',
                      label: 'Delivered',
                      date: formatRefillDateAbbreviated(refill.delivery_date)
                    },
                  ]
            }))
        },

    });
};

export const getPrescriptionMedicationQueryOptions = (id?: string) => {
    return queryOptions({
        queryKey: medicationsKeys.prescription(id),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/medicals/medications/prescriptions/${id}`);

                const result = await handleResponse<GetPersonalMedicalResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },
        enabled: !!id,
    });
};

export const getMedicationRefillQueryOptions = (id?: string) => {
    return queryOptions({
        queryKey: medicationsKeys.refill(id),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/medicals/medications/refill/${id}`);

                const result = await handleResponse<GetRefillResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },
        enabled: !!id,
    });
};

export const getPrescriptionMedicationByMedicationIdQueryOptions = (prescriptionId: string, medicationId: string) => {
    return queryOptions({
        queryKey: medicationsKeys.prescriptionMedication(prescriptionId, medicationId),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/medicals/medications/${prescriptionId}/${medicationId}`);

                const result = await handleResponse<GetPrescriptionMedicationResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },
        enabled: !!prescriptionId && !!medicationId,
    });
};

export const notificationReminderValues = () => {
    return queryOptions({
        queryKey: ReminderKeys.settings(),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/users/settings/reminder`);

                const result = await handleResponse<GetNotificationReminderValueResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },

    });
};

